import { ProductSlug } from "@/api/product-api";
import { DeviceType } from "@/services/ContentsService";
import FootDeviceIcon from "@/v2-components/core/icon/FootDeviceIcon";
import GloveDeviceIcon from "@/v2-components/core/icon/GloveDeviceIcon";
import HandDeviceIcon from "@/v2-components/core/icon/HandDeviceIcon";
import HeadDeviceIcon from "@/v2-components/core/icon/HeadDeviceIcon";
import { SleeveDeviceIcon } from "@/v2-components/core/icon/sleeve-device-icon";
import VestDeviceIcon from "@/v2-components/core/icon/VestDeviceIcon";

export default class DeviceUtil {
  public static getFriendlyName = (device: DeviceType): string =>
    DEVICE_TYPE_TABLE[device]?.name ?? "Unknown Device";

  public static getIcon = (device: DeviceType) => {
    switch (device) {
      case DeviceType.Arm:
        return SleeveDeviceIcon;
      case DeviceType.Foot:
        return FootDeviceIcon;
      case DeviceType.Glove:
        return GloveDeviceIcon;
      case DeviceType.Hand:
        return HandDeviceIcon;
      case DeviceType.Head:
        return HeadDeviceIcon;
      default:
        return VestDeviceIcon;
    }
  };

  /** 게임이 지원하는 기기 종류 → **판매 기기 제품** slug(번들은 `pickSupportedBundles`). 표는 `DEVICE_TYPE_TABLE`. */
  public static getSupportedDeviceSlugs = (
    devices: DeviceType[],
  ): ProductSlug[] => [
    ...new Set(devices.flatMap((d) => DEVICE_TYPE_TABLE[d]?.products ?? [])),
  ];

  /**
   * 「지원 기기」에 넣을 **번들** — 기기로만 이뤄지고(게임 키·배지 없음) 구성 기기가 **전부** 이 게임의 지원 기기인 것.
   *
   * 종전엔 「조끼+팔 지원이면 FPS·Starter」를 손으로 박아, 판매 중인 Starter Camo · FPS + TactVisor 가 빠졌다
   * (prod 2026-10-19). 구성품으로 판정하면 새 기기 번들도 자동으로 들어온다. 입력은 `useValidBundles()` 결과여야
   * 한다(`docs/bundle-region-policy.md` — 통화·판매 가능 게임 필터).
   * 구성 기기가 2개 미만인 번들(장갑 사이즈 세트 등)은 「번들」이 아니라 단품 옵션이라 뺀다.
   */
  public static pickSupportedBundles = <
    T extends {
      slug: string;
      skuOptions: { components: string[]; gameResells?: unknown[] }[];
    },
  >(
    bundles: T[],
    products: { slug: string; skuOptions: { sku: string }[] }[],
    deviceSlugs: readonly string[],
  ): T[] => {
    const allDevices = new Set<string>(
      Object.values(DEVICE_TYPE_TABLE).flatMap((r) => r.products),
    );
    const supported = new Set(deviceSlugs);
    const slugOfSku = new Map<string, string>();
    for (const p of products)
      for (const o of p.skuOptions) slugOfSku.set(o.sku, p.slug);
    return bundles.filter((b) => {
      if (b.skuOptions.some((o) => (o.gameResells?.length ?? 0) > 0))
        return false;
      const comps = new Set(
        b.skuOptions
          .flatMap((o) => o.components)
          .map((sku) => slugOfSku.get(sku))
          .filter((slug): slug is string => !!slug && slug !== b.slug),
      );
      if (comps.size < 2) return false;
      return [...comps].every((c) => allDevices.has(c) && supported.has(c));
    });
  };

  /**
   * 게임별 「지원 기기」 번들 교체(`GAME_SUPPORTED_DEVICE_SWAPS`) — `sellable` 에 바꿔 넣을 제품이 있을 때만,
   * 대상의 **자리에** 넣는다(대상이 없으면 맨 뒤). 이미 목록에 있으면 넣지 않는다(중복 카드 방지).
   */
  public static applySupportedDeviceSwaps = <T extends { slug: string }>(
    list: T[],
    swaps: readonly { replace: string; with: string }[] | undefined,
    sellable: readonly T[],
  ): T[] => {
    let out = list;
    for (const swap of swaps ?? []) {
      const next = sellable.find((p) => p.slug === swap.with);
      if (!next || out.some((p) => p.slug === swap.with)) continue;
      const at = out.findIndex((p) => p.slug === swap.replace);
      out =
        at < 0
          ? [...out, next]
          : [...out.slice(0, at), next, ...out.slice(at + 1)];
    }
    return out;
  };

  /**
   * 「지원 기기」 목록 순서 — **장갑(DK3)을 맨 앞**에, 나머지는 받은 순서(상품 API 순서) 그대로.
   *
   * 장갑을 지원하는 게임은 손 상호작용이 핵심이라(VRChat 의 쓰다듬기·포옹 등) 장갑이 첫 제안이어야 한다.
   * 종전엔 상품 API 순서라 VRChat 에서 FPS Bundle 이 맨 앞이었다(prod 2026-10-16). 게임 이름이 아니라
   * 「장갑 지원 여부」로 가른다 — 새 장갑 게임이 등록돼도 그대로 적용된다. 웹·앱 웹뷰가 같은 순서를 쓴다.
   */
  public static orderSupportedDevices = <T extends { slug: string }>(
    products: T[],
  ): T[] => {
    const isGlove = (p: T) =>
      p.slug === ProductSlug.TACTGLOVE_DK3 ||
      p.slug === ProductSlug.TACTGLOVE_DK2;
    return [
      ...products.filter(isGlove),
      ...products.filter((p) => !isGlove(p)),
    ];
  };
}

/**
 * 기기 종류 → 「게임 정보」 이름 + 「지원 기기」 판매 제품 — **두 섹션이 같은 표**를 쓴다.
 *
 * 종전엔 이름(`getFriendlyName`)과 제품(`getSupportedDeviceSlugs`)이 따로 적혀 어긋났다(prod 2026-10-19):
 * 조끼 제품에 판매 중인 TactSuit Air Camo 가 없었고(조끼 지원 게임 402개 전부), 장갑은 판매 종료 DK2 만 있었다(#6043).
 * Hand(Tactosy for Hands)는 지원 사실이라 이름은 두고, 판매 제품이 없어 제품은 비운다(사용자 결정 2026-10-19).
 * 목록에 없는 제품·가격 없는 제품은 호출처의 상품·가격 필터가 거른다(DK2 를 남겨 둔 이유).
 */
export const DEVICE_TYPE_TABLE: Record<
  DeviceType,
  { name: string; products: ProductSlug[] }
> = {
  [DeviceType.Vest]: {
    name: "TactSuit Pro & Air",
    products: [
      ProductSlug.TACTSUIT_PRO,
      ProductSlug.TACTSUIT_AIR,
      ProductSlug.TACTSUIT_AIR_CAMO,
    ],
  },
  [DeviceType.Head]: { name: "TactVisor", products: [ProductSlug.TACTVISOR] },
  [DeviceType.Arm]: { name: "TactSleeve", products: [ProductSlug.TACTSLEEVE] },
  [DeviceType.Foot]: {
    name: "Tactosy for Feet",
    products: [ProductSlug.TACTOSY_FOR_FEET],
  },
  [DeviceType.Glove]: {
    name: "TactGlove",
    products: [ProductSlug.TACTGLOVE_DK3, ProductSlug.TACTGLOVE_DK2],
  },
  [DeviceType.Hand]: { name: "Tactosy for Hands", products: [] },
};
